import type { ContactFormSchema } from "@agroproj/backend/convex/validation/crm";

export type RuralPropertySituation =
  | "rented"
  | "own"
  | "third_party"
  | "leased"
  | "loaned"
  | "settlement";

export type CompanyCnpjField = "companyName" | "tradingName";

export type CompanyCnpjConflict = {
  currentValue: string;
  field: CompanyCnpjField;
  label: string;
  officialValue: string;
};

type CompanyCnpjDraft = Record<CompanyCnpjField, string>;

type OfficialCnpjCompany = {
  legalName?: string;
  tradeName?: string;
};

const companyCnpjFieldDefinitions = [
  { field: "companyName", label: "Razão social", officialField: "legalName" },
  { field: "tradingName", label: "Nome fantasia", officialField: "tradeName" },
] as const;

function normalizeComparedCompanyValue(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleUpperCase("pt-BR");
}

function normalizeOfficialCnpjCompanyValue(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;

  const normalized = value.trim();
  const withoutWhitespace = normalized.replace(/\s/g, "");
  if (!withoutWhitespace || /^\*+$/.test(withoutWhitespace)) return undefined;

  return normalized;
}

export function formatInfoSimplesPrice(price: string | undefined): string | undefined {
  if (!price) return undefined;

  const sanitized = price.replace(/[^\d,.-]/g, "");
  if (!/\d/.test(sanitized)) return price;
  const normalized = sanitized.includes(",")
    ? sanitized.replace(/\./g, "").replace(",", ".")
    : sanitized;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return price;

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/**
 * Applies empty official fields directly while reserving non-empty divergences for user review.
 */
export function planCnpjCompanyUpdate(
  current: CompanyCnpjDraft,
  official: OfficialCnpjCompany,
): {
  conflicts: CompanyCnpjConflict[];
  safePatch: Partial<CompanyCnpjDraft>;
} {
  const conflicts: CompanyCnpjConflict[] = [];
  const safePatch: Partial<CompanyCnpjDraft> = {};

  for (const definition of companyCnpjFieldDefinitions) {
    const officialValue = normalizeOfficialCnpjCompanyValue(official[definition.officialField]);
    if (!officialValue) continue;

    const currentValue = current[definition.field].trim();
    if (!currentValue) {
      safePatch[definition.field] = officialValue;
      continue;
    }

    if (
      normalizeComparedCompanyValue(currentValue) !== normalizeComparedCompanyValue(officialValue)
    ) {
      conflicts.push({
        currentValue,
        field: definition.field,
        label: definition.label,
        officialValue,
      });
    }
  }

  return { conflicts, safePatch };
}

export function normalizeRuralPropertySituation(
  situation: string | undefined,
): RuralPropertySituation {
  if (situation === "owned" || situation === "own") {
    return "own";
  }
  if (situation === "lease" || situation === "leased") {
    return "leased";
  }
  if (
    situation === "rented" ||
    situation === "third_party" ||
    situation === "loaned" ||
    situation === "settlement"
  ) {
    return situation;
  }
  return "own";
}

function getErrorDataMessage(error: Error & { data?: unknown }): string | undefined {
  if (typeof error.data === "string" && error.data.trim()) {
    return error.data.trim();
  }
  if (
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data &&
    typeof error.data.message === "string" &&
    error.data.message.trim()
  ) {
    return error.data.message.trim();
  }
  return undefined;
}

export function getUserFacingErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) {
    return fallback;
  }

  const dataMessage = getErrorDataMessage(error);
  if (dataMessage) {
    return dataMessage;
  }

  const message = error.message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .find(
      (line) =>
        !line.startsWith("[CONVEX") &&
        !line.startsWith("Request ID:") &&
        !line.startsWith("at ") &&
        line !== "Called by client" &&
        line !== "Server Error",
    )
    ?.replace(/^(?:Uncaught\s+)?(?:ConvexError|Error):\s*/, "")
    .trim();

  if (
    !message ||
    message.includes("ArgumentValidationError") ||
    message.includes("Request ID:") ||
    message.includes("Called by client")
  ) {
    return fallback;
  }

  return message;
}

export function createProducerContactDefaults(producerId: string): ContactFormSchema {
  return {
    name: "",
    position: "",
    department: "",
    phone1: "",
    phone2: "",
    email: "",
    socialLinkedin: "",
    socialWhatsapp: "",
    birthDate: undefined,
    preferredContactMethod: "email",
    leadSource: "",
    role: "primary",
    observation: "",
    producerId,
  };
}

export function cleanDocumentValue(value: string): string {
  return value.replace(/\D/g, "");
}

export type ProducerDocumentType = "cpf" | "cnpj";
export type RuralRegistryKind = "cib" | "nirf";
export type RuralRegistryReference = { kind: RuralRegistryKind; value: string };

export function normalizeRuralRegistryReference(
  value: unknown,
): RuralRegistryReference | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as { kind?: unknown; value?: unknown };
  if (
    (candidate.kind !== "cib" && candidate.kind !== "nirf") ||
    typeof candidate.value !== "string"
  ) {
    return undefined;
  }
  const normalizedValue = candidate.value.trim();
  if (!isValidRuralRegistryValue(normalizedValue)) return undefined;
  return { kind: candidate.kind, value: normalizedValue };
}

export function createRuralRegistryReference(
  kind: RuralRegistryKind | undefined,
  value: string,
): RuralRegistryReference | undefined {
  return normalizeRuralRegistryReference(kind ? { kind, value } : undefined);
}

export function ruralRegistryReferenceMatches(
  current: RuralRegistryReference | undefined,
  persisted: RuralRegistryReference | undefined,
): boolean {
  return Boolean(
    current && persisted && current.kind === persisted.kind && current.value === persisted.value,
  );
}

export function normalizeProducerDocumentType(
  value: string | undefined,
): ProducerDocumentType | undefined {
  return value === "cpf" || value === "cnpj" ? value : undefined;
}

export function isValidRuralRegistryValue(value: string): boolean {
  const normalized = cleanDocumentValue(value);
  return normalized.length >= 6 && normalized.length <= 14;
}

export function getRuralRegistryValidationMessage(
  value: string,
  label: "CIB" | "NIRF",
): string | undefined {
  if (!value.trim()) return undefined;
  const normalized = cleanDocumentValue(value);
  if (normalized.length < 6 || normalized.length > 14) {
    return `${label} inválido`;
  }
  return undefined;
}

export function getNirfValidationPairMessage(cib: string, nirf: string): string | undefined {
  const hasCib = cib.trim().length > 0;
  const hasNirf = nirf.trim().length > 0;
  if (!hasCib && !hasNirf) {
    return "Informe o CIB ou o NIRF";
  }
  const cibError = hasCib ? getRuralRegistryValidationMessage(cib, "CIB") : undefined;
  if (cibError) return cibError;
  const nirfError = hasNirf ? getRuralRegistryValidationMessage(nirf, "NIRF") : undefined;
  if (nirfError) return nirfError;
  return undefined;
}

export function isValidIbamaDocument(documentType: "cpf" | "cnpj", value: string): boolean {
  const normalized = cleanDocumentValue(value);
  const expectedLength = documentType === "cpf" ? 11 : 14;
  return normalized.length === expectedLength;
}

export function getIbamaDocumentValidationMessage(
  documentType: "cpf" | "cnpj",
  value: string,
): string | undefined {
  if (!value.trim()) return undefined;
  if (!isValidIbamaDocument(documentType, value)) {
    return `${documentType.toUpperCase()} inválido`;
  }
  return undefined;
}

export function isValidIbamaInfractionYear(year: number): boolean {
  const currentYear = new Date().getUTCFullYear();
  return Number.isInteger(year) && year >= 1980 && year <= currentYear;
}

export function getIbamaYearValidationMessage(year: number | undefined): string | undefined {
  if (year === undefined) return "Ano é obrigatório";
  if (!isValidIbamaInfractionYear(year)) {
    return "Ano inválido para consulta de autuações ambientais";
  }
  return undefined;
}

export function formatComplianceConsultedAt(timestamp: number): string {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(timestamp));
}
